import type { AudioManager, Sfx } from '../audio/audio';
import type { SongName } from '../audio/music';
import { GAME, PLAYER, SCORE, TILE } from '../config/config';
import type { InputManager } from '../core/input';
import { overlaps, sign } from '../core/math';
import { Rng } from '../core/rng';
import { Enemy } from '../entities/enemy';
import type { Entity } from '../entities/entity';
import { Grub } from '../entities/grub';
import {
  EmberSeed,
  HeartPod,
  Pickup,
  PopCoin,
  PrismShard,
  Sunberry,
  Sunbit,
} from '../entities/items';
import { EntityManager } from '../entities/manager';
import { Mover } from '../entities/mover';
import { Nipper } from '../entities/nipper';
import type { EnergyOrb } from '../entities/orb';
import { Player, type Form } from '../entities/player';
import { Snug } from '../entities/snug';
import { C, type ThemeName } from '../gfx/palette';
import { ParticleSystem } from '../gfx/particles';
import type { DecorItem, Renderer } from '../gfx/renderer';
import { Camera } from '../world/camera';
import type { AreaDef, LevelDef, WarpDef } from '../world/leveldata';
import type { SolidBox } from '../world/physics';
import { TileMap } from '../world/tilemap';
import { T, isBreakableTile, isBumpableTile } from '../world/tiles';
import type { BlockSource, World } from './world';

export interface Session {
  score: number;
  coins: number;
  lives: number;
}

export interface LevelOptions {
  checkpoints: boolean;
  backtrack: boolean;
  debug: boolean;
}

export interface ClearResult {
  levelName: string;
  timeLeft: number;
  timeBonus: number;
  coins: number;
  score: number;
  flagBonus: number;
}

export type LevelPhase = 'play' | 'warpIn' | 'warpOut' | 'dead' | 'clear' | 'done';

interface AreaRuntime {
  def: AreaDef;
  map: TileMap;
  entities: EntityManager;
  movers: Mover[];
  decor: DecorItem[];
  theme: ThemeName;
  music: SongName;
  snapshot: Uint8Array;
  /** Flagpole position, if this area contains the goal. */
  goal: { x: number; topY: number; groundY: number } | null;
}

interface BlockBump {
  tx: number;
  ty: number;
  t: number;
}

const BUMP_TIME = 0.22;
const BUMP_HEIGHT = 6;
const WARP_TIME = 0.72;

/**
 * A running level: owns the areas, the player, the camera and all the rules that connect
 * them. Implements `World`, which is the only surface entities can touch.
 */
export class Level implements World {
  readonly rng: Rng;
  readonly particles: ParticleSystem;
  readonly camera: Camera;
  readonly player: Player;

  phase: LevelPhase = 'play';
  timeLeft: number;
  /** Set once the level has been completed; read by the Game scene. */
  result: ClearResult | null = null;
  /** Set when the death animation has finished. */
  respawnPending = false;
  /** True when the player died because the clock ran out. */
  diedFromTime = false;

  private areas = new Map<string, AreaRuntime>();
  private current!: AreaRuntime;
  private bumps: BlockBump[] = [];
  private freezeTimer = 0;
  private deadTimer = 0;
  private stompChain = 0;
  private shellChain = 0;
  private warp: WarpDef | null = null;
  private checkpointIndex = -1;
  private spawnForm: Form = 'small';
  private warnPlayed = false;
  private musicName: SongName | null = null;
  private flagBannerY = 0;
  private flagFired = false;
  private fireworkTimer = 0;
  private fireworks = 0;
  private coinBlockLeft = new Map<number, number>();

  constructor(
    readonly def: LevelDef,
    readonly audio: AudioManager,
    readonly input: InputManager,
    readonly session: Session,
    public options: LevelOptions,
  ) {
    this.rng = new Rng(def.seed ?? 0x5eed1234);
    this.particles = new ParticleSystem(this.rng);
    this.camera = new Camera(this.rng);
    this.camera.allowBacktrack = options.backtrack;
    this.timeLeft = def.time;
    this.player = new Player(def.spawn.x * TILE, def.spawn.y * TILE);
    this.buildAreas();
    this.enterArea(def.start, true);
    this.player.resetTo(def.spawn.x * TILE, (def.spawn.y + 1) * TILE, 'small');
    this.snapCamera();
  }

  /* ------------------------------------------------------------ world API */

  get map(): TileMap {
    return this.current.map;
  }

  get theme(): ThemeName {
    return this.current.theme;
  }

  get entities(): Entity[] {
    return this.current.entities.list;
  }

  get time(): number {
    return this.timeLeft;
  }

  set time(v: number) {
    this.timeLeft = v;
  }

  get playerIsSmall(): boolean {
    return this.player.form === 'small';
  }

  get playerHasFire(): boolean {
    return this.player.form === 'fire';
  }

  get playerCenterX(): number {
    return this.player.centerX;
  }

  get playerBottom(): number {
    return this.player.bottom;
  }

  add(e: Entity): void {
    this.current.entities.add(e);
  }

  sfx(name: Sfx): void {
    this.audio.play(name);
  }

  shake(amount: number): void {
    this.camera.addShake(amount);
  }

  freeze(seconds: number): void {
    this.freezeTimer = Math.max(this.freezeTimer, seconds);
  }

  addScore(points: number, x?: number, y?: number): void {
    if (points <= 0) return;
    this.session.score += points;
    if (x !== undefined && y !== undefined) {
      this.particles.float(x, y - 6, String(points), C.white);
    }
  }

  addCoin(count = 1): void {
    for (let i = 0; i < count; i++) {
      this.session.coins++;
      if (this.session.coins >= SCORE.coinsPerLife) {
        this.session.coins -= SCORE.coinsPerLife;
        this.grantLife(this.player.centerX, this.player.y);
      }
    }
  }

  addLife(x: number, y: number): void {
    this.grantLife(x, y);
  }

  private grantLife(x: number, y: number): void {
    this.session.lives++;
    this.audio.play('oneup');
    this.particles.float(x, y - 8, '1UP', C.pod);
  }

  nextStompScore(): number {
    const i = Math.min(this.stompChain, SCORE.stompChain.length - 1);
    this.stompChain++;
    return SCORE.stompChain[i];
  }

  nextShellScore(): number {
    const i = Math.min(this.shellChain, SCORE.shellChain.length - 1);
    this.shellChain++;
    return SCORE.shellChain[i];
  }

  resetCombo(): void {
    this.stompChain = 0;
    this.shellChain = 0;
  }

  solidBoxes(): readonly SolidBox[] {
    return this.current.movers;
  }

  warpAt(tx: number, ty: number, enter: 'down' | 'right'): WarpDef | null {
    for (const w of this.current.def.warps ?? []) {
      if (w.enter !== enter) continue;
      const ww = w.w ?? 2;
      const wh = w.h ?? 1;
      if (tx >= w.x && tx < w.x + ww && ty >= w.y && ty < w.y + wh) return w;
    }
    return null;
  }

  beginWarp(w: WarpDef): void {
    if (this.phase !== 'play') return;
    this.warp = w;
    this.phase = 'warpIn';
    this.player.startPipe(w.enter === 'down' ? 'down' : 'right', WARP_TIME);
    this.audio.play('pipe');
  }

  onPlayerDeath(): void {
    if (this.phase === 'dead') return;
    this.phase = 'dead';
    this.deadTimer = 0;
    this.audio.stopMusic();
    this.audio.play('die');
    this.musicName = null;
  }

  onFlagpole(poleX: number, groundY: number): void {
    if (this.phase !== 'play' || this.flagFired) return;
    this.flagFired = true;
    this.phase = 'clear';
    this.flagBannerY = this.player.y;
    // Height bonus: grabbing higher up the pole is worth more.
    const frac = Math.max(0, Math.min(1, (groundY - this.player.bottom) / (10 * TILE)));
    const bonus = [100, 200, 400, 800, 1000, 2000, 4000, 5000][Math.floor(frac * 7.99)];
    this.addScore(bonus, this.player.centerX, this.player.y);
    this.flagBonusValue = bonus;
    this.player.startFlagSlide(poleX + 2, groundY);
    this.audio.stopMusic();
    this.audio.play('flag');
    this.musicName = null;
  }

  private flagBonusValue = 0;

  reachedCheckpoint(index: number): void {
    if (!this.options.checkpoints) return;
    if (index <= this.checkpointIndex) return;
    this.checkpointIndex = index;
    this.spawnForm = this.player.form;
    this.audio.play('select');
    this.particles.float(this.player.centerX, this.player.y - 10, 'CHECK!', C.pod);
  }

  /* -------------------------------------------------------------- loading */

  private buildAreas(): void {
    this.areas.clear();
    for (const area of this.def.areas) {
      const map = new TileMap(area.rows);
      const runtime: AreaRuntime = {
        def: area,
        map,
        entities: new EntityManager(),
        movers: (area.movers ?? []).map((m) => new Mover(m)),
        decor: (area.decor ?? []).map((d) => ({ ...d })),
        theme: area.theme,
        music: area.music ?? (area.theme === 'cave' ? 'CAVERN' : 'OVERWORLD'),
        snapshot: map.snapshot(),
        goal: findGoal(map),
      };
      this.spawnAreaEntities(runtime);
      this.areas.set(area.id, runtime);
    }
  }

  private spawnAreaEntities(area: AreaRuntime): void {
    area.entities.clear();
    for (const s of area.map.spawns) {
      switch (s.type) {
        case 'grub':
          area.entities.add(new Grub(s.x, s.y));
          break;
        case 'snug':
          area.entities.add(new Snug(s.x, s.y));
          break;
        case 'nipper':
          area.entities.add(new Nipper(s.x, s.y));
          break;
        case 'coin':
          area.entities.add(new Sunbit(s.x, s.y));
          break;
        default:
          break;
      }
    }
  }

  private enterArea(id: string, initial = false): void {
    const area = this.areas.get(id);
    if (!area) throw new Error(`unknown area ${id}`);
    this.current = area;
    this.camera.reset(area.map.pixelW, area.map.pixelH, this.camera.x, this.camera.y);
    if (!initial) this.particles.clear();
    this.playAreaMusic();
  }

  private playAreaMusic(): void {
    const name: SongName = this.player.prism > 0 ? 'PRISM' : this.current.music;
    if (this.musicName !== name) {
      this.musicName = name;
      this.audio.playSong(name, true);
    }
  }

  private snapCamera(): void {
    this.camera.snapTo(this.player.centerX, this.player.centerY);
  }

  /** Full reset used when restarting the level from scratch. */
  restart(fromCheckpoint: boolean): void {
    this.bumps.length = 0;
    this.coinBlockLeft.clear();
    this.freezeTimer = 0;
    this.deadTimer = 0;
    this.warp = null;
    this.flagFired = false;
    this.fireworks = 0;
    this.fireworkTimer = 0;
    this.result = null;
    this.respawnPending = false;
    this.diedFromTime = false;
    this.warnPlayed = false;
    this.resetCombo();
    this.particles.clear();
    this.rng.reset();

    for (const area of this.areas.values()) {
      area.map.restore(area.snapshot);
      this.spawnAreaEntities(area);
      for (const m of area.movers) m.reset();
    }

    const cp = fromCheckpoint && this.options.checkpoints ? this.checkpointOrNull() : null;
    if (!fromCheckpoint) {
      this.checkpointIndex = -1;
      this.spawnForm = 'small';
    }
    const areaId = cp?.area ?? this.def.start;
    const sx = cp ? cp.x : this.def.spawn.x;
    const sy = cp ? cp.y : this.def.spawn.y;

    this.timeLeft = this.def.time;
    this.phase = 'play';
    this.musicName = null;
    this.enterArea(areaId, true);
    this.player.resetTo(sx * TILE, (sy + 1) * TILE, cp ? this.spawnForm : 'small');
    this.camera.allowBacktrack = this.options.backtrack;
    this.snapCamera();
  }

  private checkpointOrNull() {
    const list = this.def.checkpoints ?? [];
    if (this.checkpointIndex < 0 || this.checkpointIndex >= list.length) return null;
    return list[this.checkpointIndex];
  }

  /* --------------------------------------------------------------- update */

  update(dt: number): void {
    if (this.freezeTimer > 0) {
      this.freezeTimer -= dt;
      this.player.update(dt, this); // keeps transform timers ticking
      this.particles.update(dt);
      this.camera.updateShake(dt);
      return;
    }

    switch (this.phase) {
      case 'play':
        this.updatePlay(dt);
        break;
      case 'warpIn':
        this.player.update(dt, this);
        if (this.player.pipeTimer <= 0) this.finishWarpIn();
        break;
      case 'warpOut':
        this.player.update(dt, this);
        if (this.player.pipeTimer <= 0) {
          this.player.endPipe();
          this.phase = 'play';
        }
        break;
      case 'dead':
        this.deadTimer += dt;
        this.player.update(dt, this);
        if (this.deadTimer >= PLAYER.respawnDelay) this.respawnPending = true;
        break;
      case 'clear':
        this.updateClear(dt);
        break;
      case 'done':
        this.particles.update(dt);
        break;
    }

    this.updateBumps(dt);
    this.particles.update(dt);
    this.camera.updateShake(dt);
  }

  private updatePlay(dt: number): void {
    // clock
    this.timeLeft -= dt * GAME.timerRate;
    if (this.timeLeft <= GAME.timeWarn && !this.warnPlayed) {
      this.warnPlayed = true;
      this.audio.play('warn');
    }
    if (this.timeLeft <= 0) {
      this.timeLeft = 0;
      this.diedFromTime = true;
      this.player.kill(this, false);
      return;
    }

    for (const m of this.current.movers) m.update(dt);
    this.player.update(dt, this);
    this.current.entities.update(dt, this, this.camera);

    this.collidePlayer();
    this.collideEntities();
    this.checkFlag();
    this.checkCheckpoints();

    this.camera.follow(this.player.centerX, this.player.centerY, dt);
    this.playAreaMusic();
  }

  private updateClear(dt: number): void {
    this.player.update(dt, this);
    for (const m of this.current.movers) m.update(dt);
    this.current.entities.update(dt, this, this.camera);
    this.camera.follow(this.player.centerX, this.player.centerY, dt);

    // the banner rides down the pole with the player
    const goal = this.current.goal;
    if (goal) this.flagBannerY = Math.min(goal.groundY - 18, this.flagBannerY + 90 * dt);

    // celebration fireworks
    this.fireworkTimer -= dt;
    if (this.fireworkTimer <= 0 && this.fireworks < 6) {
      this.fireworks++;
      this.fireworkTimer = 0.4;
      const x = this.camera.x + this.rng.range(40, 216);
      const y = this.camera.y + this.rng.range(24, 90);
      this.particles.firework(x, y, this.rng.pick([C.gold, C.prism, C.berryLight, C.white]));
      this.audio.play('coin');
    }

    if (this.player.flagDone && !this.result) {
      const timeLeft = Math.max(0, Math.floor(this.timeLeft));
      const timeBonus = timeLeft * SCORE.timeBonus;
      this.addScore(timeBonus);
      this.result = {
        levelName: this.def.name,
        timeLeft,
        timeBonus,
        coins: this.session.coins,
        score: this.session.score,
        flagBonus: this.flagBonusValue,
      };
      this.phase = 'done';
      this.audio.playJingle('CLEAR', null);
    }
  }

  private finishWarpIn(): void {
    const w = this.warp;
    if (!w) {
      this.phase = 'play';
      return;
    }
    this.enterArea(w.to.area);
    const tx = w.to.x * TILE;
    const ty = w.to.y * TILE;
    const p = this.player;
    if (w.to.exit === 'up') {
      // Pipe lips are two tiles wide: centre the player, then rise out of it.
      p.x = Math.round(tx + TILE - p.w / 2);
      p.y = ty - p.h + 18; // starts buried in the pipe and rises to stand on the lip
      p.startPipe('up', WARP_TIME);
    } else if (w.to.exit === 'right') {
      p.x = tx - 18;
      p.y = ty - p.h;
      p.startPipe('right', WARP_TIME);
    } else {
      p.x = tx;
      p.y = ty;
      p.endPipe();
    }
    this.warp = null;
    this.snapCamera();
    this.phase = w.to.exit === 'drop' ? 'play' : 'warpOut';
  }

  /* ----------------------------------------------------------- collisions */

  private collidePlayer(): void {
    const p = this.player;
    if (p.busy && p.state !== 'transform') return;
    for (const e of this.current.entities.list) {
      if (!e.alive || !e.awake) continue;
      if (e.h <= 0 || e.w <= 0) continue;
      if (!overlaps(p.rect, e.rect)) continue;
      switch (e.kind) {
        case 'coin':
          (e as Sunbit).collect(this);
          break;
        case 'item':
          (e as Pickup).collect(this, p);
          this.audio.play('sprout');
          break;
        case 'enemy':
          this.touchEnemy(e as Enemy);
          break;
        default:
          break;
      }
      if (p.state === 'dying') return;
    }
  }

  private touchEnemy(en: Enemy): void {
    const p = this.player;
    if (en.dying || en.tumbling) return;

    if (p.invincible) {
      en.onPrism?.(this);
      return;
    }

    // Stomp test: descending, with the feet above the enemy's shoulders when the
    // step began. Comparing against the *previous* position is what makes this robust
    // at low fall speeds, where a single step barely overlaps the boxes.
    const stomping = p.vy > 0 && p.prevBottom <= en.y + 6;
    if (stomping && en.onStomp) {
      if (en.onStomp(this)) {
        p.stompBounce(this);
        return;
      }
    }

    // An idle shell is kicked rather than harmful.
    if (en instanceof Snug && en.state === 'shell') {
      const dir = sign(en.centerX - p.centerX) || p.facing;
      en.kick(this, dir);
      this.addScore(400, en.centerX, en.y);
      return;
    }

    if (p.invulnerable) return;
    p.hurt(this);
  }

  private collideEntities(): void {
    const list = this.current.entities.list;
    // orbs vs enemies
    for (const o of list) {
      if (o.kind !== 'orb' || !o.alive) continue;
      for (const e of list) {
        if (e.kind !== 'enemy' || !e.alive || !e.awake || e.h <= 0) continue;
        if (!overlaps(o.rect, e.rect)) continue;
        const en = e as Enemy;
        if (en.onOrb?.(this)) (o as EnergyOrb).pop(this);
        break;
      }
    }
    // enemy vs enemy
    for (let i = 0; i < list.length; i++) {
      const a = list[i];
      if (a.kind !== 'enemy' || !a.alive || !a.awake) continue;
      for (let j = i + 1; j < list.length; j++) {
        const b = list[j];
        if (b.kind !== 'enemy' || !b.alive || !b.awake) continue;
        if (!overlaps(a.rect, b.rect)) continue;
        const ea = a as Enemy;
        const eb = b as Enemy;
        const aSlide = ea instanceof Snug && ea.sliding;
        const bSlide = eb instanceof Snug && eb.sliding;
        if (aSlide && !bSlide) eb.onShellHit?.(this, ea.facing);
        else if (bSlide && !aSlide) ea.onShellHit?.(this, eb.facing);
        else if (!aSlide && !bSlide) ea.bumpInto(eb);
      }
    }
  }

  private checkFlag(): void {
    const p = this.player;
    const tx0 = Math.floor(p.x / TILE);
    const tx1 = Math.floor((p.x + p.w - 1) / TILE);
    const ty0 = Math.floor(p.y / TILE);
    const ty1 = Math.floor((p.y + p.h - 1) / TILE);
    for (let ty = ty0; ty <= ty1; ty++) {
      for (let tx = tx0; tx <= tx1; tx++) {
        const id = this.map.get(tx, ty);
        if (id !== T.FLAGPOLE && id !== T.FLAG_TOP) continue;
        // find the ground under the pole
        let gy = ty;
        while (gy < this.map.h && !this.map.solid(tx, gy)) gy++;
        this.onFlagpole(tx * TILE, gy * TILE);
        return;
      }
    }
  }

  private checkCheckpoints(): void {
    const list = this.def.checkpoints ?? [];
    for (let i = 0; i < list.length; i++) {
      const cp = list[i];
      if ((cp.area ?? this.def.start) !== this.current.def.id) continue;
      if (this.player.centerX >= cp.x * TILE) this.reachedCheckpoint(i);
    }
  }

  /* --------------------------------------------------------------- blocks */

  hitBlock(tx: number, ty: number, source: BlockSource, strong: boolean): void {
    const id = this.map.get(tx, ty);
    if (!isBumpableTile(id)) {
      if (source === 'player') this.audio.play('bump');
      return;
    }

    const px = tx * TILE;
    const py = ty * TILE;

    if (isBreakableTile(id) && strong) {
      this.map.set(tx, ty, T.EMPTY);
      this.particles.brickShards(px, py, C.brick, C.brickDark);
      this.audio.play('break');
      this.shake(2.2);
      this.addScore(SCORE.brick, px + TILE / 2, py);
      this.killOnTop(tx, ty);
      return;
    }

    this.pushBump(tx, ty);
    this.killOnTop(tx, ty);

    switch (id) {
      case T.BRICK:
        this.audio.play('bump');
        this.shake(0.9);
        break;
      case T.BRICK_COINS: {
        const key = this.map.key(tx, ty);
        const left = this.coinBlockLeft.get(key) ?? 8;
        this.spawnCoin(px, py);
        if (left <= 1) this.map.set(tx, ty, T.USED);
        else this.coinBlockLeft.set(key, left - 1);
        break;
      }
      case T.QUESTION:
      case T.HIDDEN_COIN:
        this.spawnCoin(px, py);
        this.map.set(tx, ty, T.USED);
        break;
      case T.QUESTION_LIFE:
      case T.HIDDEN_LIFE: {
        const pod = new HeartPod(px + 1, py);
        pod.startEmerge(py);
        this.add(pod);
        this.map.set(tx, ty, T.USED);
        this.audio.play('sprout');
        break;
      }
      case T.BRICK_ITEM:
      case T.QUESTION_ITEM: {
        this.spawnPowerup(px, py);
        this.map.set(tx, ty, T.USED);
        break;
      }
      default:
        this.audio.play('bump');
        break;
    }
  }

  /** Choose the reward based on the player's current form. */
  private spawnPowerup(px: number, py: number): void {
    let item: Pickup;
    if (this.player.form === 'small') item = new Sunberry(px + 1, py);
    else if (this.player.form === 'big') item = new EmberSeed(px + 1, py);
    else item = new PrismShard(px + 1, py);
    item.startEmerge(py);
    this.add(item);
    this.audio.play('sprout');
  }

  private spawnCoin(px: number, py: number): void {
    this.add(new PopCoin(px, py - 4));
    this.addCoin(1);
    this.addScore(SCORE.coin, px + TILE / 2, py - 10);
    this.audio.play('coin');
  }

  /** Enemies standing on a bumped block are knocked out. */
  private killOnTop(tx: number, ty: number): void {
    const left = tx * TILE;
    const right = left + TILE;
    const top = ty * TILE;
    for (const e of this.current.entities.list) {
      if (e.kind !== 'enemy' || !e.alive || !e.awake) continue;
      if (e.bottom < top - 6 || e.bottom > top + 6) continue;
      if (e.x + e.w <= left || e.x >= right) continue;
      (e as Enemy).onBlockBump?.(this);
    }
  }

  private pushBump(tx: number, ty: number): void {
    const existing = this.bumps.find((b) => b.tx === tx && b.ty === ty);
    if (existing) {
      existing.t = 0;
      return;
    }
    this.bumps.push({ tx, ty, t: 0 });
  }

  private updateBumps(dt: number): void {
    for (let i = this.bumps.length - 1; i >= 0; i--) {
      const b = this.bumps[i];
      b.t += dt;
      if (b.t >= BUMP_TIME) this.bumps.splice(i, 1);
    }
  }

  private bumpOffset = (tx: number, ty: number): number => {
    for (const b of this.bumps) {
      if (b.tx === tx && b.ty === ty) {
        return Math.sin((b.t / BUMP_TIME) * Math.PI) * BUMP_HEIGHT;
      }
    }
    return 0;
  };

  /* ----------------------------------------------------------------- draw */

  draw(r: Renderer): void {
    r.setCamera(this.camera);
    r.drawSky(this.theme);
    r.drawDecor(this.current.decor);
    r.drawTiles(this.map, this.theme, Math.floor(this.timeLeft * 4), this.bumpOffset);
    for (const m of this.current.movers) m.draw(r, this);
    this.drawGoalBanner(r);
    this.drawCheckpoints(r);
    this.current.entities.draw(r, this, this.camera);
    if (this.player.state !== 'done') this.player.draw(r);
    this.particles.draw(r);
    if (this.options.debug) this.drawDebug(r);
  }

  private drawGoalBanner(r: Renderer): void {
    const goal = this.current.goal;
    if (!goal) return;
    const y = this.flagFired ? this.flagBannerY : goal.topY + 6;
    r.blit(r.sprites.get('banner'), goal.x + 10, y);
  }

  private drawCheckpoints(r: Renderer): void {
    const list = this.def.checkpoints ?? [];
    for (let i = 0; i < list.length; i++) {
      const cp = list[i];
      if ((cp.area ?? this.def.start) !== this.current.def.id) continue;
      const x = cp.x * TILE;
      const y = cp.y * TILE;
      r.rectWorld(x + 7, y - 2, 2, TILE + 2, C.stoneDark);
      r.blit(r.sprites.get(`pennant.${i <= this.checkpointIndex ? 1 : 0}`), x + 9, y - 2);
    }
  }

  private drawDebug(r: Renderer): void {
    const p = this.player;
    r.frameWorld(p.x, p.y, p.w, p.h, C.red);
    for (const e of this.current.entities.list) {
      if (!e.alive || !e.awake || e.h <= 0) continue;
      r.frameWorld(e.x, e.y, e.w, e.h, e.kind === 'enemy' ? '#ff8f4a' : '#5ce8f0');
    }
    for (const m of this.current.movers) r.frameWorld(m.x, m.y, m.w, m.h, '#f5c33b');
    // tile grid + solid tiles
    const x0 = Math.floor(this.camera.x / TILE);
    const x1 = x0 + Math.ceil(r.w / TILE) + 1;
    const y0 = Math.floor(this.camera.y / TILE);
    const y1 = y0 + Math.ceil(r.h / TILE) + 1;
    for (let ty = y0; ty <= y1; ty++) {
      for (let tx = x0; tx <= x1; tx++) {
        if (this.map.solid(tx, ty)) r.frameWorld(tx * TILE, ty * TILE, TILE, TILE, '#4a3f6b');
        else if (this.map.oneWay(tx, ty)) r.frameWorld(tx * TILE, ty * TILE, TILE, 4, '#67c23a');
      }
    }
    // warp triggers
    for (const w of this.current.def.warps ?? []) {
      r.frameWorld(w.x * TILE, w.y * TILE, (w.w ?? 2) * TILE, (w.h ?? 1) * TILE, '#e8407a');
    }
  }

  /** Area id for the HUD / debug readout. */
  get areaId(): string {
    return this.current.def.id;
  }

  get activeEntityCount(): number {
    return this.current.entities.activeCount;
  }

  get totalEntityCount(): number {
    return this.current.entities.list.length;
  }

}

/** Locate the flagpole in a map, if it has one. */
function findGoal(map: TileMap): { x: number; topY: number; groundY: number } | null {
  for (let ty = 0; ty < map.h; ty++) {
    for (let tx = 0; tx < map.w; tx++) {
      if (map.get(tx, ty) !== T.FLAG_TOP) continue;
      let gy = ty;
      while (gy < map.h && !map.solid(tx, gy)) gy++;
      return { x: tx * TILE, topY: ty * TILE, groundY: gy * TILE };
    }
  }
  return null;
}
